/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'utils/action.util';
import {
  EVDialog,
  EVBox,
  EVDialogContent,
  EVTypography,
  EVSnackbar,
  EVAlert,
} from '@eagleview/ev-comp-library';
import PhoneNumberComponent from 'features/view-profile-dialog/component/Phone.component';
import validateUserInput from 'utils/validateUserInput.util';
import UserProfile from 'commonComponents/UserProfile';
import TextField from 'commonComponents/Textfield';
import useStyles from './ViewProfileDialogStyle';
import * as actions from './ViewProfileDialog.actions';
import selector from './ViewProfileDialog.selector';
import ViewDetailsComponent from './component/ViewDetails.component';

/**
 * Component to be used to view and update user details.
 */
const ViewProfileDialog = (props) => {
  const {
    t, viewProfile, setViewProfile, userDetails, userErrorMessages, showToast, severity, errorToastMessage, errorCode, countryList, isImpersonated,
    actions: {
      setErrorMessages, clearHelperTextMessages, clearErrorMessage, updateProfileDetails, closeToastMessage, getCountries,
    },
  } = props;
  const classes = useStyles();
  const [userProfileDetails, setUserProfileDetails] = useState(userDetails);

  /**
   * Close user profile panel
   */
  const handleClose = () => {
    setViewProfile(false);
    setUserProfileDetails(userDetails);
    clearHelperTextMessages();
  };

  /**
   * Sets the state with entered value in text field
   * @param {String} fieldId - id of the text field which triggerd handleChange
   * @param {Event} event - event triggered on changing value of text fields
   */
  const handleChange = (fieldId, event) => {
    const inputVal = { ...userProfileDetails };
    const { value } = event.target;
    inputVal[fieldId] = value;
    setUserProfileDetails(inputVal);
    if (userErrorMessages[fieldId]) {
      clearErrorMessage(fieldId);
    }
  };

  /**
   * Set of action to be performed when the text field on blur event is triggered
   * @param {Event} event - event triggered on losing focus of text field
   */
  const handleBlur = ({ target }) => {
    const { id, value } = target;
    const requestBody = {};
    const errMsgs = validateUserInput(id, value);
    setErrorMessages(id, errMsgs);
    if (!errMsgs) {
      if (id !== 'phoneNumber' && userDetails[id] !== userProfileDetails[id]) {
        requestBody[id] = value;
        updateProfileDetails(requestBody);
      } else if (userDetails.phoneNumber !== userProfileDetails.phoneNumber || userDetails.countryId !== userProfileDetails.countryId) {
        requestBody.phoneNumber = userProfileDetails.phoneNumber;
        requestBody.countryId = userProfileDetails.countryId;
        updateProfileDetails(requestBody);
      }
    }
  };

  useEffect(() => {
    setUserProfileDetails(userDetails);
  }, [userDetails]);

  useEffect(() => {
    getCountries();
  }, []);

  /**
   * Method triggered to close the snackbar
   */
  const handleSnackbarClose = () => {
    closeToastMessage();
  };

  const alertMsg = severity !== 'error'
    ? t('UserDetailsSuccessfullyUpdated')
    : `${errorCode}: ${errorToastMessage} ${t('UserDetailsUpdateError')}}`;

  return (
    <>
      <EVDialog
        id="viewProfileDialog"
        open={viewProfile}
        onClose={handleClose}
        className={classes.container}
      >
        <EVDialogContent className={classes.dialogContent}>
          <EVBox className={classes.box}>
            <UserProfile monogramVariant="large" userDetails={userProfileDetails} />
          </EVBox>
          <EVBox>
            <EVTypography id="companyName" className={classes.companyName}>
              {userProfileDetails.organization ? userProfileDetails.organization.name : ''}
            </EVTypography>
          </EVBox>
          {isImpersonated ? (
            <ViewDetailsComponent userProfileDetails={userProfileDetails} />
          ) : (
            <EVBox>
              <EVBox>
                <TextField
                  textFieldInputProps={{
                    className: classes.input,
                    inputProps: { maxLength: 50 },
                  }}
                  textFieldId="firstName"
                  textFieldValue={userProfileDetails.firstName}
                  textFieldLabel="First name"
                  handleChange={(event) => handleChange('firstName', event)}
                  handleBlur={handleBlur}
                  helperText={userErrorMessages.firstName}
                />
              </EVBox>
              <EVBox>
                <TextField
                  textFieldInputProps={{
                    className: classes.input,
                    inputProps: { maxLength: 50 },
                  }}
                  textFieldId="lastName"
                  textFieldValue={userProfileDetails.lastName}
                  textFieldLabel="Last name"
                  handleChange={(event) => handleChange('lastName', event)}
                  handleBlur={handleBlur}
                  helperText={userErrorMessages.lastName}
                />
              </EVBox>
              <EVBox>
                <TextField
                  textFieldInputProps={{
                    className: classes.input,
                    readOnly: true,
                  }}
                  textFieldId="email"
                  textFieldValue={userProfileDetails.userName}
                  textFieldLabel="Email"
                />
              </EVBox>
              <EVBox>
                <TextField
                  textFieldInputProps={{
                    className: classes.input,
                    inputProps: { maxLength: 100 },
                  }}
                  textFieldId="title"
                  textFieldValue={userProfileDetails.title}
                  textFieldLabel="Job Title"
                  handleChange={(event) => handleChange('title', event)}
                  handleBlur={handleBlur}
                  helperText={userErrorMessages.title}
                />
              </EVBox>
              <EVBox>
                <PhoneNumberComponent
                  countryList={countryList}
                  setUserProfileDetails={setUserProfileDetails}
                  userProfileDetails={userProfileDetails}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  userErrorMessages={userErrorMessages}
                />
              </EVBox>
            </EVBox>
          )}
        </EVDialogContent>
      </EVDialog>
      <EVSnackbar
        open={showToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={10000}
        onClose={handleSnackbarClose}
      >
        <EVAlert severity={severity}>
          <div dangerouslySetInnerHTML={{ __html: alertMsg }} />
        </EVAlert>
      </EVSnackbar>
    </>
  );
};

ViewProfileDialog.defaultProps = {
  setViewProfile: null,
};

ViewProfileDialog.propTypes = {
  /**
   * @property {Function} t - A method which is triggered for translation.
   */
  t: PropTypes.func.isRequired,
  /**
   * @property {Object} userDetails - details of the logged in user.
   */
  userDetails: PropTypes.shape().isRequired,
  /**
   * @property {Bool} viewProfile - to show / hide view profile dialog box.
   */
  viewProfile: PropTypes.bool.isRequired,
  /**
   * @property {Function} setViewProfile - to set show / hide property to view profile dialog box.
   */
  setViewProfile: PropTypes.func,
  /**
   * @property {Object} actions - Object containing the list of actions that are dispatched by the component
   */
  actions: PropTypes.shape({
    setErrorMessages: PropTypes.func,
    clearHelperTextMessages: PropTypes.func,
    clearErrorMessage: PropTypes.func,
    updateProfileDetails: PropTypes.func,
    closeToastMessage: PropTypes.func,
    getCountries: PropTypes.func,
  }).isRequired,
  /**
   * @property {Object} userErrorMessages - Object containing the list of error messages for the fields in the component
   */
  userErrorMessages: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    title: PropTypes.string,
    phoneNumber: PropTypes.string,
    countryId: PropTypes.string,
  }).isRequired,
  /**
   * @property {Bool} showToast - state to decide when to show the success toast message
   */
  showToast: PropTypes.bool.isRequired,
  /**
   * @property {String} severity - to decide it snackbar will display success or error message
   */
  severity: PropTypes.string.isRequired,
  /**
   * @property {array} countryList - get country list.
   */
  countryList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * @property {String} errorToastMessage - Message to be displayed when error occurs
   */
  errorToastMessage: PropTypes.string.isRequired,
  /**
   * @property {String} errorCode - Message to be displayed when error occurs
   */
  errorCode: PropTypes.string.isRequired,

  /**
   * @property {Boolean} isImpersonated - store value indicating whether the user is impersonated or not
   */
  isImpersonated: PropTypes.bool,
};

ViewProfileDialog.defaultProps = {
  isImpersonated: false,
};

export default compose(withTranslation(), connect(selector, bindActionCreators(actions)))(ViewProfileDialog);
